"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const vec_1 = require("../common/vec");
const CreaseEdgeLoop_1 = require("../core/CreaseEdgeLoop");
const CreaseEdgeRef_1 = require("../core/CreaseEdgeRef");
const MetaCrease_1 = require("./MetaCrease");
const MetaEdge_1 = require("./MetaEdge");
const MetaFace_1 = require("./MetaFace");
const MetaVertex_1 = require("./MetaVertex");
// AdobePatentID="P8499-US-ORG1"
/**
 * Debug flag: set to true for debug output.
 */
const showDebug = false;
/**
 * Logs debug information to the console if showDebug is true.
 */
function logDebug(...messages) {
    if (showDebug) {
        /* eslint-disable-next-line no-console */
        console.debug(' ', ...messages);
    }
}
/**
 * A helper class that analyzes a Crease object and attemps to infer fold angles.
 */
class CreaseAnalyzer {
    /**
     * Attempts to automatically infer fold angles for the current Crease object.
     * @param {Crease} crease A Crease object.
     */
    analyze(crease) {
        this._crease = crease;
        const metaVertices = crease.vertices.map((vertex, i) => new MetaVertex_1.MetaVertex(i, vertex));
        const metaEdges = crease.edges.map((edge, i) => new MetaEdge_1.MetaEdge(i, edge, metaVertices[edge.vertex1.index], metaVertices[edge.vertex2.index]));
        const metaFaces = crease.faces.map((face, i) => new MetaFace_1.MetaFace(i, face, face.edgeLoops.map(loop => new CreaseEdgeLoop_1.CreaseEdgeLoop(loop.edgeRefs.map(edgeRef => new CreaseEdgeRef_1.CreaseEdgeRef(metaEdges[edgeRef.edge.index], edgeRef.isReversed))))));
        // Init crease and add connectivity.
        this._metaCrease = new MetaCrease_1.MetaCrease(metaVertices, metaEdges, metaFaces);
        // Remove any decorative edges.
        let tol = this._removeFaceDecorations();
        // let tol2 = this._calcThicknessOffsetTol();
        // TODO: we need to do this better.
        tol = 10;
        // TODO: still experimenting with the order of the following operations.
        // Merge vertices within a tol.
        if (tol) {
            this._mergeVertices(tol);
            this._mergeParallelEdges();
        }
        // Mark initial tab assignments in MetaCrease.
        this._markInitialTabFaces();
        // Fold gussets.
        this._foldGussets();
        this._solve();
        // Vertices with multiple attached tabs may be mislabelled.
        if (this._unmarkMislabeledTabs()) {
            this._solve();
        }
        let count = Infinity;
        while (this.unsolvedEdges.length > 0 && count > 0) {
            count = 0;
            // Vertices whose non tab faces sum to ~360 have at least one tab.
            if (this._mark360Tabs()) {
                count += this._solve();
            }
            // Merge parallel edges which may have been missed.
            if (this._mergeParallelEdges()) {
                count += this._solve();
            }
            // // Continue to mark terminal nodes as tabs.
            // if (this._markTerminalNodesAsTabs()) {
            //     count += this._solve();
            // }
            // Check if anything's changed.
            logDebug(`Vertices solved this round: ${count}`);
        }
        this._metaCrease.edges.forEach((edge) => {
            if (edge.isCrease && edge.foldAngle === null) {
                logDebug(`No foldAngle found for edge ${edge.toString()}`);
            }
            else {
                edge.propagateFoldAngle();
            }
        });
    }
    get vertices() {
        // Return all non-tab vertices.
        return this._metaCrease.vertices.filter(vertex => !(vertex.incidentFaces.length === 1 && vertex.incidentFaces[0].isTab));
    }
    get edges() {
        // Return all non-tab edges.
        return this._metaCrease.edges.filter(edge => !(edge.incidentFaces.length === 1 && edge.incidentFaces[0].isTab));
    }
    get faces() {
        // Return all non-tab faces.
        return this._metaCrease.faces.filter(face => !face.isTab);
    }
    get unsolvedEdges() {
        return this._metaCrease.edges.filter(edge => edge.foldAngle === null);
    }
    _solve() {
        let count = 0;
        // Create a queue containing all the vertices that could become three-face corners when
        // folded.
        const queue = this._metaCrease.vertices.filter(vertex => vertex.isSolvable);
        logDebug('Initial queue:\n     ', queue.map(v => v.toString()).join('\n      '));
        // Continue processing the queue of vertices until it's empty.
        while (queue.length) {
            // Pop a vertex off the queue and analyze its neighborhood.
            const vertex = queue.shift();
            // Check that this vertex has not already been deallocated.
            if (vertex.isDestroyed) {
                continue;
            }
            logDebug('Analyzing: ', vertex.toString());
            const { success, nextVertex } = vertex.solve(logDebug);
            // If we came up with fold angles, increase our count.
            if (success) {
                // Cleanup.
                this._reindexElements();
                count += 1;
                // If a new vertex is returned by analyzeVertex, add it to the queue.
                if (nextVertex) {
                    logDebug('    Adding: ', nextVertex.toString());
                    queue.push(nextVertex);
                }
            }
        }
        return count;
    }
    _reindexElements() {
        // First remove anything that has been destoyed.
        for (let i = this._metaCrease.faces.length - 1; i >= 0; i--) {
            const face = this._metaCrease.faces[i];
            if (face.isDestroyed) {
                this._metaCrease.faces.splice(i, 1);
            }
        }
        for (let i = this._metaCrease.edges.length - 1; i >= 0; i--) {
            const edge = this._metaCrease.edges[i];
            if (edge.isDestroyed) {
                this._metaCrease.edges.splice(i, 1);
            }
        }
        for (let i = this._metaCrease.vertices.length - 1; i >= 0; i--) {
            const vertex = this._metaCrease.vertices[i];
            if (vertex.isDestroyed) {
                this._metaCrease.vertices.splice(i, 1);
            }
        }
        this._metaCrease.vertices.forEach((vertex, i) => {
            vertex._setIndex(i);
        });
        this._metaCrease.edges.forEach((edge, i) => {
            edge._setIndex(i);
        });
        this._metaCrease.faces.forEach((face, i) => {
            face._setIndex(i);
        });
    }
    _mergeVertices(tolerance) {
        let changeMade = false;
        this._metaCrease.edges.forEach((edge) => {
            if (edge.length > tolerance) {
                return;
            }
            this._collapseEdge(edge);
            changeMade = true;
        });
        if (changeMade) {
            this._reindexElements();
        }
    }
    /* eslint-disable-next-line class-methods-use-this */
    _collapseEdge(edge) {
        const { vertex1, vertex2 } = edge;
        // if (vertex1 === vertex2) {
        //     // This edge has already been collapsed.
        //     return;
        // }
        // edge refs of vertex 1 and 2 corresponding to edge
        const edgeRef1 = vertex1.incidentEdges.find(edgeRef => edgeRef.edge === edge);
        const edgeRef2 = vertex2.incidentEdges.find(edgeRef => edgeRef.edge === edge);
        // Vertex1 can equal vertex2 if we are collapsing a hole.
        if (vertex1 !== vertex2) {
            logDebug('    Merging vertices ', vertex1.toString(), ' to ', vertex2.toString());
            vertex1.merge(vertex2, edgeRef1, edgeRef2);
        }
        // Delete edge.
        logDebug('    Removing edge ', edge.toString());
        edge.remove();
    }
    _calcThicknessOffsetTol() {
        // Find shortest edge between two vertices attached to creases.
        // Also compute average crease length.
        let avgLength = 0;
        let numEdgesInAvg = 0;
        let shortestEdge;
        this._metaCrease.edges.forEach((edge) => {
            // Check if both edges are attached to a crease.
            if (edge.vertex1.isConnectedToCrease || edge.vertex2.isConnectedToCrease) {
                if (!shortestEdge || edge.length < shortestEdge.length) {
                    shortestEdge = edge;
                }
            }
            if (!edge.isCrease) {
                return;
            }
            avgLength += edge.length;
            numEdgesInAvg += 1;
        });
        avgLength /= numEdgesInAvg;
        if (shortestEdge && shortestEdge.length * 20 < avgLength) {
            return shortestEdge.length * 5;
        }
        return undefined;
    }
    _unmarkMislabeledTabs() {
        let changeMade = false;
        // Find any unsolved vertices with multiple tabs, unmark all but the smallest tab face.
        this.vertices.forEach((vertex) => {
            if (vertex.isSolved) {
                return;
            }
            const { incidentFaces, nonTabIncidentFaces } = vertex;
            // if (nonTabIncidentFaces.length !== 4) {
            //     return;
            // }
            const tabFaces = incidentFaces.filter(face => nonTabIncidentFaces.indexOf(face) < 0);
            if (tabFaces.length < 2) {
                return;
            }
            // It's possible only one of the incident faces should be marked as a tab.
            // Pick the smallest face that is a terminal node on the dual graph.
            // TODO: The edge connected to the tab face should be ~= length to the farthest
            // non-tab face at this vertex, so that these could be joined in
            // a merge operation later.
            let smallestFaceIndex = -1;
            let smallestFaceArea = Infinity;
            tabFaces.forEach((face, i) => {
                if (face.incidentFaces.length > 1) {
                    return;
                }
                // Get area from CreaseFace wrapped by MetaFace.
                const { area } = face.face;
                if (area < smallestFaceArea) {
                    smallestFaceArea = area;
                    smallestFaceIndex = i;
                }
            });
            if (smallestFaceIndex < 0) {
                return;
            }
            changeMade = true;
            tabFaces.forEach((face, i) => {
                if (i === smallestFaceIndex) {
                    return;
                }
                face.isTab = false;
                // MetaFace.isTab does an additional check before a face is unmarked as a tab.
                // check that this change was successful.
                if (face.isTab) {
                    logDebug(`Face ${face.toString()} unmarked as tab`);
                }
            });
            changeMade = true;
        });
        return changeMade;
    }
    _mark360Tabs() {
        let changeMade = false;
        // Find any vertices whose non-tab faces sum up to ~360 degrees.
        // One of these faces should be a tab.
        this.vertices.forEach((vertex) => {
            if (vertex.deactivated) {
                return;
            }
            const { nonTabIncidentFaces } = vertex;
            if (nonTabIncidentFaces.length < 4) {
                return;
            }
            const { incidentFaceInfo } = vertex;
            // Find total angular contribution of non tab incident faces.
            let sum = 0;
            incidentFaceInfo.forEach((info) => {
                sum += Math.acos(info.cos);
            });
            // Total angular contribution of ~2Pi.
            // TODO: what should this tol be?
            const tol = 0.1;
            if (sum > Math.PI * 2 - tol) {
                // One of the incident faces should be marked as a tab.
                // Pick the smallest face that is a terminal node on the dual graph.
                // TODO: we could also add a requirement about edge lengths: eg the edge
                // connected to the tab face should be ~= length to the farthest
                // non-tab face at this vertex
                // so that these could be joined in a merge operation later.
                let smallestFaceIndex = -1;
                let smallestFaceArea = Infinity;
                nonTabIncidentFaces.forEach((face, i) => {
                    if (face.incidentFaces.length > 1) {
                        return;
                    }
                    // Get area from CreaseFace wrapped by MetaFace.
                    const { area } = face.face;
                    if (area < smallestFaceArea) {
                        smallestFaceArea = area;
                        smallestFaceIndex = i;
                    }
                });
                if (smallestFaceIndex < 0) {
                    return;
                }
                logDebug(`Marking 360 face: ${nonTabIncidentFaces[smallestFaceIndex].toString()} as tab.`);
                nonTabIncidentFaces[smallestFaceIndex].isTab = true;
                changeMade = true;
            }
        });
        return changeMade;
    }
    _mergeParallelEdges() {
        let changeMade = false;
        // Check for parallel edges that haven't merged and merge them.
        const { edges } = this;
        for (let i = edges.length - 1; i >= 0; i--) {
            for (let j = i - 1; j >= 0; j--) {
                if (edges[i].hasSameVertices(edges[j])) {
                    logDebug(`Merging edges: ${edges[i].toString()} to edge ${edges[j].toString()}`);
                    edges[j].mergeParallel(edges[i]);
                    changeMade = true;
                }
            }
        }
        if (changeMade) {
            this._reindexElements();
        }
        return changeMade;
    }
    _markTerminalNodesAsTabs() {
        let changeMade = false;
        // Mark any terminal nodes connected by an unsolved edges as tabs.
        // Store connectivity before making changes to face tab states.
        const initialAdjacentFaces = this.faces.map(face => face.nonTabIncidentFaces);
        this.faces.forEach((face, i) => {
            if (initialAdjacentFaces[i].length === 1) {
                let adjacentToSolvedEdge = false;
                face.edgeLoops[0].edgeRefs.forEach((edgeRef) => {
                    if (edgeRef.edge.foldAngle !== null) {
                        adjacentToSolvedEdge = true;
                    }
                });
                if (!adjacentToSolvedEdge) {
                    face.isTab = true;
                    logDebug(`Marking terminal face: ${face.toString()} as tab.`);
                    changeMade = true;
                }
            }
        });
        return changeMade;
    }
    // TODO:The following function is not called yet.
    /*
    _removeCreaseInterruptions(): void {
        this._metaCrease.edges.forEach((edge) => {
            if (edge.edges.length > 1) {
                // Ignore this edge if it's already been merged.
                return;
            }
            if (!edge.isCrease) {
                return;
            }

            type MetaFacePair = [MetaFace | undefined, MetaFace | undefined];
            const edgeSharesSameFaces = (edgeRef: CreaseEdgeRef, faces: MetaFacePair) => {
                if (!edgeRef.isCrease && faces.indexOf(undefined) < 0) {
                    return edgeRef.leftFace === faces[0] || edgeRef.rightFace === faces[1]
                        || edgeRef.leftFace === faces[1] || edgeRef.rightFace === faces[0];
                }
                return (edgeRef.leftFace === faces[0] && edgeRef.rightFace === faces[1])
                    || (edgeRef.leftFace === faces[1] && edgeRef.rightFace === faces[0]);
            };

            const buildList = (currentEdges: MetaEdge[], currentMetaVertex: MetaVertex,
                faces: MetaFacePair, preferredFace?: MetaFace): MetaEdge[][] => {
                // check that vertex is valid
                if (currentMetaVertex.incidentEdges.filter(edgeRef =>
                    edgeRef.isCrease).length > 1) {
                    return [currentEdges];
                }

                // check that we haven't looped around
                if (currentEdges.length > 1 && currentMetaVertex.incidentEdges.map(edgeRef =>
                    edgeRef.edge).indexOf(currentEdges[0]) > -1) {
                    return [currentEdges.slice(0, currentEdges.length - 1)];
                }

                const allPaths: MetaEdge[][] = [];

                currentMetaVertex.incidentEdges.forEach((edgeRef) => {
                    // Check that edge is valid
                    const edge = edgeRef.edge as MetaEdge;
                    if (
                        edge === currentEdges[currentEdges.length - 1] // Same as the prev.
                        || !edgeSharesSameFaces(edgeRef, faces) // Same face connectivity.
                        || currentEdges.indexOf(edge) >= 0 // This edge has been visited.
                        || edge.edges.length > 1 // This edge has already been merged.
                    ) {
                        return;
                    }
                    // // Check that this works geometrically
                    // let endingPos = edge.edges[0].vertex1.position;
                    // if (vec.dot(vec.subtract(endingPos, startingPos), edgeVec) < 0) {
                    //     return;
                    // }
                    // endingPos = edge.edges[0].vertex2.position;
                    // if (vec.dot(vec.subtract(endingPos, startingPos), edgeVec) < 0) {
                    //     return;
                    // }

                    if (!preferredFace) {
                        if (!edgeRef.isCrease) {
                            preferredFace = (edgeRef.leftFace || edgeRef.rightFace) as MetaFace;
                        }
                    } else if (!edgeRef.isCrease && !edgeSharesSameFaces(
                        edgeRef,
                        [preferredFace, undefined],
                    )) {
                        // check that preferred face is satisfied
                        return;
                    }

                    const nextPaths = buildList(
                        currentEdges.concat([edge]),
                        edgeRef.vertex2 as MetaVertex,
                        faces,
                        preferredFace,
                    );
                    nextPaths.forEach((path) => {
                        if (path.length > 0) {
                            allPaths.push(path);
                        }
                    });
                });
                return allPaths;
            };
        });
    }
    */
    _removeFaceDecorations() {
        // Get CreaseVertex for MetaVertex.  This function only works for
        // MetaEdges that contain only a single edge.
        const creaseVertexForMetaVertex = (metaEdge, metaVertex) => {
            if (metaEdge.edges.length > 1) {
                throw new Error(`Cannot find MetaVertex ${metaVertex.toString()} on merged edge ${metaEdge.toString()}.`);
            }
            if (metaEdge.vertex1 === metaVertex) {
                return metaEdge.edges[0].vertex1;
            }
            if (metaEdge.vertex2 === metaVertex) {
                return metaEdge.edges[0].vertex2;
            }
            throw new Error(`MetaVertex: ${metaVertex.toString()} not connected to MetaEdge: ${metaEdge.toString()}`);
        };
        const collinearEdges = (edge1, edge2) => {
            if (edge1 === edge2) {
                return true;
            }
            const edgeVec1 = vec_1.vec.normalize(vec_1.vec.subtract(creaseVertexForMetaVertex(edge1, edge1.vertex2).position, creaseVertexForMetaVertex(edge1, edge1.vertex1).position));
            const edgeVec2 = vec_1.vec.normalize(vec_1.vec.subtract(creaseVertexForMetaVertex(edge2, edge2.vertex2).position, creaseVertexForMetaVertex(edge2, edge2.vertex1).position));
            // Test with tol of about 2.5 degrees.
            if (Math.abs(vec_1.vec.dot(edgeVec1, edgeVec2)) < 0.999) {
                // Not parallel.
                return false;
            }
            let edgeVec3 = vec_1.vec.subtract(creaseVertexForMetaVertex(edge2, edge2.vertex1).position, creaseVertexForMetaVertex(edge1, edge1.vertex1).position);
            if (vec_1.vec.lengthSquared(edgeVec3) === 0) {
                edgeVec3 = vec_1.vec.subtract(creaseVertexForMetaVertex(edge2, edge2.vertex2).position, creaseVertexForMetaVertex(edge1, edge1.vertex1).position);
            }
            edgeVec3 = vec_1.vec.normalize(edgeVec3);
            // Test with tol of about 2.5 degrees.
            if (Math.abs(vec_1.vec.dot(edgeVec1, edgeVec3)) < 0.999) {
                // Not collinear.
                return false;
            }
            return true;
        };
        const matchedEdgeRefs = (edgeRef1, edgeRef2) => {
            if (edgeRef1 === edgeRef2) {
                return false;
            }
            const edge1 = edgeRef1.edge;
            const edge2 = edgeRef2.edge;
            const edgeVec1 = vec_1.vec.normalize(vec_1.vec.subtract(creaseVertexForMetaVertex(edge1, edgeRef1.vertex2).position, creaseVertexForMetaVertex(edge1, edgeRef1.vertex1).position));
            const edgeVec2 = vec_1.vec.normalize(vec_1.vec.subtract(creaseVertexForMetaVertex(edge2, edgeRef2.vertex2).position, creaseVertexForMetaVertex(edge2, edgeRef2.vertex1).position));
            // Test with tol of about 2.5 degrees.
            if (vec_1.vec.dot(edgeVec1, edgeVec2) < 0.999) {
                // Not parallel.
                return false;
            }
            const edgeVec3 = vec_1.vec.normalize(vec_1.vec.subtract(creaseVertexForMetaVertex(edge2, edgeRef2.vertex1).position, creaseVertexForMetaVertex(edge1, edgeRef1.vertex1).position));
            // Test with tol of about 2.5 degrees.
            if (vec_1.vec.dot(edgeVec1, edgeVec3) < 0.999) {
                // Not collinear.
                // Sign matters here, we also want to make sure that edgeRef2.vertex1
                // is in the direction of edgeVec1 from edgeRef1.vertex1
                return false;
            }
            return true;
        };
        // Store merge groups for all faces.
        const mergeGroupsByFace = [];
        this._metaCrease.faces.forEach((face) => {
            let groups = [];
            mergeGroupsByFace.push([]);
            face.edgeLoops[0].edgeRefs.forEach((edgeRef1, i) => {
                const edge1 = edgeRef1.edge;
                if (edge1.edges.length > 1) {
                    // Already merged.
                    return;
                }
                face.edgeLoops[0].edgeRefs.forEach((edgeRef2) => {
                    const edge2 = edgeRef2.edge;
                    if (edge2.edges.length > 1) {
                        // Already merged.
                        return;
                    }
                    // Find pairs of collinear edgeRefs with same direction
                    if (!matchedEdgeRefs(edgeRef1, edgeRef2)) {
                        return;
                    }
                    // Get chain of edges from edgeRef1 to edgeRef2
                    const group = [];
                    for (let index = 0; index < face.edgeLoops[0].edgeRefs.length; index++) {
                        const nextEdge = face.edgeLoops[0]
                            .edgeRefs[(i + index) % face.edgeLoops[0].edgeRefs.length]
                            .edge;
                        if (nextEdge.edges.length > 1) {
                            // Contains a crease that has already been merged.
                            return;
                        }
                        group.push(nextEdge);
                        if (nextEdge === edgeRef2.edge) {
                            break;
                        }
                    }
                    // Check that first and last creases in this edge are colinear.
                    let firstCrease;
                    let lastCrease;
                    for (let index = 0; index < group.length; index++) {
                        const edge = group[index];
                        if (!edge.isCrease) {
                            continue;
                        }
                        if (!firstCrease) {
                            firstCrease = edge;
                        }
                        lastCrease = edge;
                    }
                    // TODO: Is it possible for firstCrease or lastCrease to be undefined?
                    // @ts-ignore
                    if (!collinearEdges(firstCrease, lastCrease)) {
                        return;
                    }
                    groups.push(group);
                });
            });
            if (groups.length === 0) {
                return;
            }
            // Sort groups by number of edges.
            groups.sort((a, b) => b.length - a.length);
            // Remove groups that are a subset of another, larger group.
            for (let i = 0; i < groups.length; i++) {
                for (let j = groups.length - 1; j > i; j--) {
                    /* eslint-disable-next-line no-loop-func */
                    if (groups[j].every(el => groups[i].indexOf(el) !== -1)) {
                        groups.splice(j, 1);
                    }
                }
            }
            groups = groups.filter(group => group.length >= 2);
            groups.forEach((group) => {
                logDebug(`Group found ${group}.`);
            });
            mergeGroupsByFace[face.index] = groups;
        });
        let mergeGroups = [];
        // Group merge groups according to adjacency.
        mergeGroupsByFace.forEach((faceGroups, faceIndex) => {
            faceGroups.forEach((group) => {
                const mergeGroup = [group];
                const addToMergeGroup = (adjacentFace, adjacentCrease) => {
                    const allFaceGroups = mergeGroupsByFace[adjacentFace.index];
                    for (let j = allFaceGroups.length - 1; j >= 0; j--) {
                        if (allFaceGroups[j].indexOf(adjacentCrease) >= 0) {
                            mergeGroup.push(allFaceGroups[j]);
                            allFaceGroups.splice(j, 1);
                        }
                    }
                };
                // Get attached faces
                const adjacentFaces = [];
                group.forEach((edge) => {
                    if (edge.leftFace && edge.leftFace.index !== faceIndex
                        && adjacentFaces.indexOf(edge.leftFace) < 0) {
                        adjacentFaces.push(edge.leftFace);
                        addToMergeGroup(edge.leftFace, edge);
                    }
                    if (edge.rightFace && edge.rightFace.index !== faceIndex
                        && adjacentFaces.indexOf(edge.rightFace) < 0) {
                        adjacentFaces.push(edge.rightFace);
                        addToMergeGroup(edge.rightFace, edge);
                    }
                });
                mergeGroups.push({
                    groups: mergeGroup,
                    length: 0,
                    // Store the longest group as the basis for the merging operation.
                    longestGroupIndex: 0,
                });
            });
        });
        if (mergeGroups.length === 0) {
            return undefined;
        }
        mergeGroups.forEach((el) => {
            const { groups } = el;
            groups.forEach((group, i) => {
                // Calculate length of new edge.
                let vertex1;
                let vertex2;
                if (group[1].vertex1 === group[0].vertex1
                    || group[1].vertex2 === group[0].vertex1) {
                    vertex1 = group[0].edges[0].vertex2;
                }
                else {
                    ({ vertex1 } = group[0].edges[0]);
                }
                if (group[group.length - 2].vertex1 === group[group.length - 1].vertex1
                    || group[group.length - 2].vertex2 === group[group.length - 1].vertex1) {
                    ({ vertex2 } = group[group.length - 1].edges[0]);
                }
                else {
                    vertex2 = group[group.length - 1].edges[0].vertex1;
                }
                const newLength = vec_1.vec.length(vec_1.vec.subtract(vertex1.position, vertex2.position));
                // Store the longest edge lengths for the group.
                if (newLength > el.length) {
                    el.length = newLength;
                    el.longestGroupIndex = i;
                }
            });
        });
        // Sort merge groups by length.
        mergeGroups = mergeGroups.sort((a, b) => b.length - a.length);
        // We can use the lengths of merged edges from this operation
        // to estimate a merge tol for this dieline.
        let averageTol;
        let numOuterEdgesCollapsed = 0;
        mergeGroups.forEach((el) => {
            const { groups } = el;
            groups.forEach((group, index) => {
                // If group has at least one crease, merge all boundary vertices away.
                if (group.filter(edge => edge.isCrease).length === 0) {
                    return;
                }
                let firstCreaseIndex;
                let lastCreaseIndex;
                group.forEach((edge, i) => {
                    if (firstCreaseIndex === undefined && edge.isCrease) {
                        firstCreaseIndex = i;
                    }
                    // TODO: Should the following line check edge.isCrease?
                    lastCreaseIndex = i;
                });
                // Merge away boundary vertices.
                for (let i = group.length - 1; i >= 0; i--) {
                    const edge = group[i];
                    // Sometimes these edges have already been removed
                    if (edge.isDestroyed) {
                        group.splice(i, 1);
                        continue;
                    }
                    if ((lastCreaseIndex && i > lastCreaseIndex)
                        || (firstCreaseIndex && i < firstCreaseIndex)) {
                        // TODO: What if averageTol is undefined?
                        // @ts-ignore
                        averageTol += edge.length;
                        numOuterEdgesCollapsed += 1;
                    }
                    if (edge.isCrease) {
                        continue;
                    }
                    this._collapseEdge(edge);
                    group.splice(i, 1);
                }
                groups[index] = group;
            });
        });
        // Check if more than two faces connect to a single merge group.
        // Fix these cases before going on to merge step.
        mergeGroups.forEach((el, j) => {
            const { groups } = el;
            if (groups.length < 2) {
                return;
            }
            const longestGroup = groups[el.longestGroupIndex];
            // Get first and last creases in the longest group.
            // All other groups should have these in common.
            // In cases where longestGroup.length === 1, firstCrease === lastCrease.
            const firstCrease = longestGroup[0];
            const lastCrease = longestGroup[longestGroup.length - 1];
            const { leftFace, rightFace } = firstCrease;
            if ((lastCrease.leftFace !== leftFace && lastCrease.leftFace !== rightFace)
                || (lastCrease.rightFace !== leftFace && lastCrease.rightFace !== rightFace)) {
                // This whole group is invalid.
                // This can happen in cases with gussets + small manufacturing offsets.
                // TODO: we may still need to handle some of these cases.
                mergeGroups[j].groups = [];
                return;
            }
            // Put longestGroup in front.
            groups.splice(el.longestGroupIndex, 1);
            groups.unshift(longestGroup);
            for (let i = groups.length - 1; i > 0; i--) {
                const group = groups[i];
                // If this group is identical to longest edge, ignore.
                if (longestGroup.length === group.length) {
                    if (group.slice().reverse().every((edge, index) => edge === longestGroup[index])) {
                        groups.splice(i, 1);
                        continue;
                    }
                }
                // Outer creases should be identical to longest group, otherwise ignore.
                if (group[0] !== longestGroup[0] && group[0]
                    !== longestGroup[longestGroup.length - 1]) {
                    groups.splice(i, 1);
                    continue;
                }
                if (group[group.length - 1] !== longestGroup[0] && group[group.length - 1]
                    !== longestGroup[longestGroup.length - 1]) {
                    groups.splice(i, 1);
                    continue;
                }
            }
            if (groups.length < 2) {
                return;
            }
            // Now we need to square the remaining groups.
            // Split any other (inner) creases with diff face connectivity.
            groups.forEach((group) => {
                for (let i = group.length - 1; i >= 0; i--) {
                    const edge = group[i];
                    // Edge may have diff orientation as longestGroup[0].
                    // We can assume these are not boundary edges, since those have been pruned.
                    let extraFace;
                    if (edge.leftFace !== leftFace && edge.leftFace !== rightFace) {
                        extraFace = edge.leftFace;
                    }
                    else if (edge.rightFace !== leftFace && edge.rightFace !== rightFace) {
                        extraFace = edge.rightFace;
                    }
                    if (!extraFace) {
                        continue;
                    }
                    // Split edge.
                    const newEdge = edge.splitParallel(extraFace, this._metaCrease.edges.length);
                    this._metaCrease.edges.push(newEdge);
                    // TODO: Duplicate and split vertices.
                    // Collapse edges on original group.
                    this._collapseEdge(edge);
                    group.splice(i, 1);
                }
            });
        });
        mergeGroups = mergeGroups.filter(el => el.groups.length > 0);
        mergeGroups.forEach((el) => {
            const { groups } = el;
            // Merge largest group.
            const group = groups[0];
            // If there are any destroyed edges, ignore.
            if (group.some(edge => edge.isDestroyed)) {
                return;
            }
            // If this won't be a successful merge, ignore.
            const { leftFace, rightFace } = group[0];
            let extraFace;
            group.forEach((edge) => {
                // Edge may have diff orientation as longestGroup[0].
                // We can assume these are not boundary edges, since those have been pruned.
                if (edge.leftFace !== leftFace && edge.leftFace !== rightFace) {
                    // TODO: MetaEdge does not have an extraFace property. Should this be leftFace?
                    // @ts-ignore
                    extraFace = edge.extraFace;
                }
                else if (edge.rightFace !== leftFace && edge.rightFace !== rightFace) {
                    extraFace = edge.rightFace;
                }
            });
            if (extraFace) {
                return;
            }
            logDebug(`Merging edges ${group}.`);
            // Merge remaining edges.
            // Even if only one edge left in group,
            // we need to set the edge's length with this operation.
            group[0].mergeSerial(group.slice(1), el.length);
        });
        // TODO: merge parallel with tab creases.
        // Cleanup.
        this._reindexElements();
        if (numOuterEdgesCollapsed > 0) {
            // TODO: Is averageTol guaranteed to be defined?
            // @ts-ignore
            return averageTol / numOuterEdgesCollapsed;
        }
        return undefined;
    }
    getFaceClassification(crease) {
        const copy = crease.copy();
        copy.edges.forEach((edge) => {
            edge.foldAngle = null;
        });
        this.analyze(copy);
        return this._metaCrease.faces.map((face) => {
            if (face.isGusset) {
                return 'G';
            }
            return face.isTab ? 'T' : 'U';
        });
    }
    /**
     * Marks all faces that appear to be tabs.
     * @private
     */
    _markInitialTabFaces() {
        this._metaCrease.faces.forEach((face) => {
            const { incidentFaces } = face;
            if (incidentFaces.length === 1) {
                if (face.area / incidentFaces[0].area < 0.5) {
                    // Tab face = terminal nodes on dual with small
                    // area relative to connecting face.
                    face.isTab = true;
                }
                // } else if (incidentFaces[0].incidentFaces.length === 2) {
                //     // Tab face = terminal nodes on dual whose adjacent face
                //     // is only connected to one other face,
                //     // smaller or equal in area to adjacent face.
                //     if (face.area <= incidentFaces[0].area) {
                //         isTab[i] = true;
                //     }
                // }
            }
        });
    }
    /**
     * Marks all faces that appear to be gussets. Sets fold angle and assignment of edges between
     * gusset faces.
     * @private
     */
    _foldGussets() {
        const isAngleBisector = (vertex, previousEdgeRef, currentEdgeRef, nextEdgeRef) => {
            const angleInfo1 = vertex.getAngularContributionBetweenEdges(previousEdgeRef, currentEdgeRef);
            const angleInfo2 = vertex.getAngularContributionBetweenEdges(currentEdgeRef, nextEdgeRef);
            const angle1 = Math.atan(angleInfo1.sin / angleInfo1.cos);
            const angle2 = Math.atan(angleInfo2.sin / angleInfo2.cos);
            if (angle1 + angle2 > Math.PI - 1) {
                return false;
            }
            return Math.abs(angle1 / angle2 - 1) < 0.1;
        };
        // Look at each vertex neighborhood to see if there's a gusset (a pair
        // of boundary faces whose shared crease edge is an angle bisector).
        this._metaCrease.vertices.forEach((vertex) => {
            // See if the current vertex has at least 4 edges, and as many faces
            // (i.e., it's not a boundary vertex).
            const vertexEdgeRefs = vertex.incidentEdges;
            const vertexFaces = vertex.incidentFaces;
            const n = vertexEdgeRefs.length;
            if (n >= 4 && vertexFaces.length === n
                && vertexEdgeRefs.every(edgeRef => edgeRef.isCrease)) {
                // See if any of the incident edges is the angle bisector of the
                // adjacent pair of edges.
                vertexEdgeRefs.forEach((currentEdgeRef) => {
                    if (!currentEdgeRef.isCrease) {
                        return;
                    }
                    const previousEdgeRef = vertex.getPreviousEdgeRef(currentEdgeRef);
                    const nextEdgeRef = vertex.getNextEdgeRef(currentEdgeRef);
                    if (isAngleBisector(vertex, previousEdgeRef, currentEdgeRef, nextEdgeRef)) {
                        // If so, make sure the edges opposite the bisected angle are boundary
                        // edges.
                        const oppVertex = currentEdgeRef.vertex2;
                        const oppEdgeRef1 = oppVertex.getPreviousEdgeRef(currentEdgeRef);
                        const oppEdgeRef2 = oppVertex.getNextEdgeRef(currentEdgeRef);
                        if (oppEdgeRef1.isBoundary && oppEdgeRef2.isBoundary) {
                            // Yup. Mark the two adjacent faces as tabs.
                            const leftFace = currentEdgeRef.leftFace;
                            const rightFace = currentEdgeRef.rightFace;
                            leftFace.isTab = true;
                            leftFace.isGusset = true;
                            rightFace.isTab = true;
                            rightFace.isGusset = true;
                            currentEdgeRef.edge.foldAngle = 180;
                            if (currentEdgeRef.edge.assignment === 'M') {
                                currentEdgeRef.edge.assignment = 'V';
                            }
                            else {
                                currentEdgeRef.edge.assignment = 'M';
                            }
                        }
                    }
                });
            }
        });
    }
}
exports.CreaseAnalyzer = CreaseAnalyzer;
//# sourceMappingURL=CreaseAnalyzer.js.map